import Grid from '@material-ui/core/Grid'
import type { Theme } from '@material-ui/core/styles'
import EditIcon from '@material-ui/icons/Edit'
import Email from '@material-ui/icons/Email'
import { Field, FastField, useFormikContext, Form } from 'formik'
import get from 'lodash/get'
import { useSnackbar } from 'notistack'
import * as React from 'react'
import { getObjectLabels, getFeature } from '@paintscout/util/builder'
import UploadImage from '../../components/UploadImage'
import Button from '../../components/Button'
import { useClientOptions } from '../../providers/ClientOptionsProvider'
import { compressFile, uploadFile } from '@paintscout/util/s3'

import { FormikInputField, FormikHtmlEditor } from '../../formik'
import { NewFormSection as FormSection } from '../../components/FormSection'
import FormSectionTitle from '../../components/FormSectionTitle'
import PhoneNumberInput, { PhoneNumberExtensionInput } from '../../components/PhoneNumberInput'
import SignatureCanvas from '../../components/SignatureCanvas'
import type { Auth0UserWithClaims } from '../../providers/UserProvider/UserContext'
import type { UserPreferencesDocument } from 'paintscout'
import { makeStyles } from '@material-ui/core'
import type { StyleClasses } from '../../styles'
import SettingsPage from '../../components/SettingsPage'
import Spinner from '../../components/Spinner'
import { useApolloClient } from '@apollo/react-hooks'
import Avatar from '../../components/Avatar'

const useStyles = makeStyles<Theme, UserProfileProps>(
  (theme) => ({
    root: {},
    contactInfo: {
      paddingBottom: theme.typography.pxToRem(15)
    },
    changeInfoButtons: {
      maxWidth: 450
    },
    changeInfoButton: {
      display: 'flex',
      justifyContent: 'left',
      marginTop: theme.spacing(4),
      [theme.breakpoints.down('xs')]: {
        marginTop: theme.spacing(0)
      }
    },
    canvasButton: {
      justifyContent: 'left'
    },
    presentation: {},
    avatarDiv: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(3.5),
      marginLeft: theme.spacing(),
      [theme.breakpoints.down('xs')]: {
        marginLeft: theme.spacing(4)
      }
    },
    defaultPadding: {
      paddingBottom: theme.spacing(1.5)
    },
    keyContainer: {
      padding: theme.spacing(2)
    },
    uploadButton: {
      marginTop: theme.spacing(3)
    }
  }),
  { name: 'UserProfileForm' }
)

export interface UserProfileProps {
  classes?: StyleClasses<typeof useStyles>
  disableSignature?: boolean
  allowEmailChange?: boolean
  showChangePassword?: boolean
  extendedSubmitting?: boolean

  onChangePassword?: () => any
}

function UserProfileForm(props: UserProfileProps) {
  const classes = useStyles(props)
  const { showChangePassword, disableSignature, allowEmailChange, extendedSubmitting } = props
  const { options, clientId } = useClientOptions()
  const { values, setFieldValue, errors } = useFormikContext<{
    user: Auth0UserWithClaims
    preferences: UserPreferencesDocument
  }>()
  const { enqueueSnackbar } = useSnackbar()
  const roles = values?.user?.app_metadata?.roles ?? []
  const canBeSales = roles.includes('sales') || roles.includes('superadmin') || roles.includes('admin')
  const objectLabels = getObjectLabels({ options })
  const hasPresentations = getFeature({ options, path: 'presentation.enabled' })
  const hasAboutYourEstimator = getFeature({ options, path: 'presentation.aboutYourEstimator' })
  const customAvatar = values?.user?.user_metadata?.customAvatar
  const userName = values?.user?.name
  const userColor = values?.user?.user_metadata?.color

  const [fileUploading, setFileUploading] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const apolloClient = useApolloClient()
  const s3UploadFeatureEnabled = getFeature({ options, path: 's3ImageUpload.enabled' })

  const handleAvatarClear = () => {
    const customAvatarValue = {
      value: '',
      cdnValue: null,
      cloudinaryId: null,
      s3PublicKey: null,
      fileType: null
    }

    setFieldValue('user.user_metadata.customAvatar', customAvatarValue)
  }
  const handleAvatarUpload = ({
    src: newSrc,
    format,
    cloudinaryPublicId,
    s3PublicKey,
    width,
    height
  }: {
    src: string
    format: string
    cloudinaryPublicId: string
    s3PublicKey: string
    width: number
    height: number
  }) => {
    const customAvatarValue = {
      value: newSrc,
      cdnValue: newSrc,
      cloudinaryId: cloudinaryPublicId,
      s3PublicKey,
      width,
      height,
      fileType: format
    }
    setFieldValue('user.user_metadata.customAvatar', customAvatarValue)
  }

  const handlePresentationUpload = (image: {
    src: string
    cloudinaryPublicId?: string
    s3PublicKey?: string
    format: string
    width: number
    height: number
    type: string
  }) => {
    setFieldValue('preferences.presentation.image' as any, image)
  }

  const handlePresentationClear = () => {
    setFieldValue('preferences.presentation.image' as any, null)
  }

  const handleSignatureChange = (event: any, data?: string) => {
    setFieldValue('preferences.signature' as any, data)
  }

  const handleSignatureUploadError = (type?: string) => {
    if (type) {
      enqueueSnackbar(`Unable to upload signature, ${type} file format is not currently supported.`, {
        variant: 'error'
      })
    } else {
      enqueueSnackbar('Unable to upload signature', { variant: 'error' })
    }
  }

  async function handleUpload(files: File[]) {
    try {
      setFileUploading(true)
      const file = await compressFile({ file: files[0] })
      const { format, width, height, src, cloudinaryPublicId, s3PublicKey } = await uploadFile({
        apolloClient,
        file,
        uploadOptions: {
          companyId: clientId,
          s3UploadFeatureEnabled
        }
      })

      handleAvatarUpload({ src, format, cloudinaryPublicId, s3PublicKey, width, height })
    } catch (e) {
      console.error(e)
      enqueueSnackbar('Unable to upload image', { variant: 'error' })
    }
    setFileUploading(false)
  }

  const handleUploadButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <SettingsPage title="Profile">
      <Form className={classes.root}>
        <FormSection>
          <Grid item xs={12}>
            <FormSectionTitle
              title={'Your Photo'}
              subTitle={'Upload an image to use as your profile photo.'}
              variant="h4"
            />
          </Grid>
          <Grid className={classes.avatarDiv} item xs={2}>
            {extendedSubmitting || fileUploading ? (
              <Spinner />
            ) : customAvatar?.value ? (
              <FastField name="user.user_metadata.customAvatar">
                {({ field }) => (
                  <UploadImage
                    avatar
                    cloudinaryPublicId={field.value?.cloudinaryId}
                    s3PublicKey={field.value?.s3PublicKey}
                    src={field.value?.value ?? field.value?.s3PublicKey ?? field.value?.cloudinaryId}
                    onUpload={handleAvatarUpload}
                    onClear={() => handleAvatarClear()}
                  />
                )}
              </FastField>
            ) : (
              <>
                <Avatar
                  backgroundColor="#FFFFFF"
                  disableTooltip
                  filled
                  avatarColor={userColor}
                  width="100px"
                  height="100px"
                  fontSize="48px"
                >
                  {/* Limit the username to two words so we only get two initials */}
                  {userName.toUpperCase().split(/\s+/).slice(0, 2).join(' ')}
                </Avatar>
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = e.target.files
                      if (files && files.length > 0) {
                        handleUpload(Array.from(files))
                      }
                    }}
                  />
                  <div className={classes.uploadButton}>
                    <Button variant="outlined" onClick={handleUploadButtonClick}>
                      + Upload
                    </Button>
                  </div>
                </>
              </>
            )}
          </Grid>
          <FormSectionTitle
            title={'Contact Information'}
            variant={'h4'}
            subTitle={`Shown on ${objectLabels.quote.plural.toLowerCase()} to help customers contact you.`}
          />
          <Grid container spacing={2} className={classes.contactInfo}>
            <Grid item xs={6} sm={5}>
              <Field
                component={FormikInputField}
                name="user.user_metadata.firstName"
                label={'First Name'}
                required
                fullWidth
                helperTextState="error"
                helperText={get(errors, 'user.user_metadata.firstName')}
              />
            </Grid>
            <Grid item xs={6} sm={5}>
              <Field
                component={FormikInputField}
                name="user.user_metadata.lastName"
                label={'Last Name'}
                required
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className={classes.contactInfo}>
            <Grid item xs={6} sm={3}>
              <Field
                component={FormikInputField}
                inputComponent={PhoneNumberInput}
                inputProps={{ separator: options?.options?.phoneNumbers?.separator }}
                name="user.user_metadata.phoneNumber"
                label={'Phone Number'}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Field
                component={FormikInputField}
                inputComponent={PhoneNumberExtensionInput}
                name="user.user_metadata.phoneExtension"
                label={'EXT.'}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Field component={FormikInputField} name="user.user_metadata.title" label={'Title'} fullWidth />
            </Grid>
            <Grid item={true} xs={12} sm={6} lg={6} xl={6}>
              <Field
                component={FormikInputField}
                name="user.email"
                disabled={!allowEmailChange}
                margin="dense"
                label={'Email'}
                icon={Email}
                fullWidth={true}
              />
            </Grid>
            {showChangePassword && (
              <Grid item={true} xs={12} sm={6}>
                <Button className={classes.changeInfoButton} icon={EditIcon} onClick={props.onChangePassword}>
                  Change Password
                </Button>
              </Grid>
            )}
          </Grid>
        </FormSection>
        {canBeSales && (
          <FormSection>
            <FormSectionTitle
              title={'Signature'}
              variant={'h4'}
              subTitle={`Place your signature here to auto-populate on accepted ${objectLabels.quote.plural.toLowerCase()}.`}
            />
            {!disableSignature ? (
              <SignatureCanvas
                newSignatureButtons
                disabled={disableSignature}
                caption={'Sign Here'}
                classes={{ captionRow: classes.canvasButton }}
                value={values.preferences?.signature ?? ''}
                onChange={handleSignatureChange}
                onUploadError={handleSignatureUploadError}
                allowUpload={true}
              />
            ) : (
              <img src={values.preferences.signature} style={{ width: '100%', maxWidth: 450, height: 150 }} />
            )}
          </FormSection>
        )}
        {hasPresentations && canBeSales && hasAboutYourEstimator && (
          <FormSection>
            <FormSectionTitle
              title={'Presentation'}
              variant={'h4'}
              subTitle={
                'More information about you, which can be added as an "About Your Estimator" section of presentations'
              }
            />
            <Grid container={true} spacing={4} className={classes.presentation}>
              <Grid item={true} xs={12} sm={6} lg={6} xl={6}>
                <UploadImage
                  cloudinaryPublicId={values?.preferences?.presentation?.image?.cloudinaryPublicId}
                  s3PublicKey={values?.preferences?.presentation?.image?.s3PublicKey}
                  src={values?.preferences?.presentation?.image?.src}
                  onUpload={handlePresentationUpload}
                  onClear={handlePresentationClear}
                />
              </Grid>
              <Grid item={true} xs={12} sm={6} lg={6} xl={6}>
                <FastField
                  component={FormikHtmlEditor}
                  name="preferences.presentation.description"
                  margin="dense"
                  label={'Description/Bio'}
                  fullWidth
                />
              </Grid>
            </Grid>
          </FormSection>
        )}
      </Form>
    </SettingsPage>
  )
}

export default UserProfileForm
